import { type QueryFunctionContext } from '@tanstack/react-query'
import { createClient } from '@/util/supabase/component'
import type { BaseSpecies } from '@/types'
import { stringNotEmptyFilterTypeGuard } from '@/util/helpers'

type QuerySpecies = BaseSpecies & { int_id: number }

export const speciesLookupQueryFn = async ({
  queryKey,
}: QueryFunctionContext<string[]>): Promise<QuerySpecies[]> => {
  const supabase = createClient()
  const input = queryKey[1]

  const [genus, species, rank, infraspecies] = input
    .split(' ')
    .map((part) => part.trim().toLowerCase())
    .filter(stringNotEmptyFilterTypeGuard)

  // assemble a postgrest query string for each input
  let query = `and(genus.ilike.${genus}*,epithet.ilike.${species}*`
  const hasInfraRank = Boolean(rank && infraspecies)
  if (hasInfraRank)
    query = `${query},infraspecies_rank.ilike.${rank}*,infraspecies_epithet.ilike.${infraspecies}*`
  else query = `${query},infraspecies_rank.is.null,infraspecies_epithet.is.null`
  console.log({ hasInfraRank, query })

  // close the parentheses
  query = `${query})`

  const { data, error } = await supabase
    .from('species')
    .select(
      `id,
        int_id,
          epithet,
          genus,
          infraspecies_epithet,
          infraspecies_rank
      `,
    )
    .or(query)
    .order('epithet')
    .order('genus')

  if (error) throw new Error(error.message)
  if (data.length > 1) {
    // find an exact match if there is one
    const bestMatch = data.find((each) => {
      return each.genus.toLowerCase() === genus &&
        each.epithet.toLowerCase() === species &&
        hasInfraRank
        ? rank === each.infraspecies_rank?.toLowerCase() &&
            infraspecies === each.infraspecies_epithet?.toLowerCase()
        : each.infraspecies_epithet === null && each.infraspecies_rank === null
    })
    if (bestMatch) return [bestMatch]
  }
  return data ?? []
}

export const referenceQueryFn = async ({
  queryKey,
}: QueryFunctionContext<string[]>) => {
  const supabase = createClient()
  const [citation, description] = queryKey.slice(1)
  const { data, error } = await supabase
    .from('references')
    .select('id')
    .eq('citation', citation)
    .eq('description', description)
    .single()
  console.log({ data, error })
  if (error) {
    if (error.details === 'The result contains 0 rows') return null
    throw new Error(error.message)
  }
  return data
}
